import { parseBirthDateFromCnId, parseGenderFromCnId } from "./cnIdPhone";

export const MAX_REGISTRATION_OPTION_QUESTIONS = 10
export const MAX_REGISTRATION_OPTION_CHOICES = 20

export type RegistrationOptionGender = '男' | '女'

/** 报名通道身份：会员自助 / 散客（含代亲朋登记与分享链接） */
export type RegistrationOptionParticipantType = 'member' | 'guest'

/** 选项参与对象条件；null 表示不限 */
export type RegistrationOptionParticipantAudience = RegistrationOptionParticipantType | null

export type RegistrationOptionSubject = {
  ageYears: number | null
  gender: RegistrationOptionGender | null
  /** 当前报名通道身份，用于参与对象条件判断 */
  participantType: RegistrationOptionParticipantType | null
  /** 会员积分余额（Members.Points）；散客为 null */
  pointsBalance?: number | null
}

export type ActivityRegistrationOptionChoice = {
  id: string
  label: string
  chargeEnabled: boolean
  /** 表单编辑用：元，最多两位小数 */
  amountYuanText: string
  /** 条件：年龄下限（含）；与 ageMaxInclusive 同时有值时生效 */
  ageMinInclusive?: number | null
  ageMaxInclusive?: number | null
  gender?: RegistrationOptionGender | null
  /** 条件：参与对象；null 表示不限 */
  participantAudience?: RegistrationOptionParticipantAudience
  /** 条件：积分余额下限（含）；可只填一侧 */
  pointsMinInclusive?: number | null
  /** 条件：积分余额上限（含）；可只填一侧 */
  pointsMaxInclusive?: number | null
  /** 表单态：年龄条件编辑区已展开（提交/API 忽略） */
  ageLimitEditing?: boolean
  /** 表单态：性别条件编辑区已展开（提交/API 忽略） */
  genderLimitEditing?: boolean
  /** 表单态：参与对象条件编辑区已展开（提交/API 忽略） */
  participantAudienceLimitEditing?: boolean
  /** 表单编辑：年龄下限文案（避免清空时编辑区收起） */
  ageMinText?: string
  /** 表单编辑：年龄上限文案 */
  ageMaxText?: string
}

export type ActivityRegistrationOptionQuestion = {
  id: string
  title: string
  choices: ActivityRegistrationOptionChoice[]
}

export type ActivityRegistrationOptionsConfig = {
  enabled: boolean
  items: ActivityRegistrationOptionQuestion[]
}

export type ActivityRegistrationOptionSelection = {
  questionId: string
  choiceId: string
}

function ageYearsFromYmd(birthYmd: string, refYmd: string): number | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(birthYmd) || !/^\d{4}-\d{2}-\d{2}$/.test(refYmd)) return null
  const [yb, mb, db] = birthYmd.split('-').map((x) => Number(x))
  const [yr, mr, dr] = refYmd.split('-').map((x) => Number(x))
  if (![yb, mb, db, yr, mr, dr].every((n) => Number.isFinite(n))) return null
  let age = yr - yb
  if (mr < mb || (mr === mb && dr < db)) age -= 1
  return age
}

function parseOptionalInt(raw: unknown): number | null {
  if (raw == null || raw === '') return null
  const n = Number(raw)
  if (!Number.isFinite(n)) return null
  return Math.trunc(n)
}

function parseOptionalGender(raw: unknown): RegistrationOptionGender | null {
  const g = String(raw ?? '').trim()
  if (g === '男' || g === '女') return g
  return null
}

function parseOptionalParticipantAudience(raw: unknown): RegistrationOptionParticipantAudience {
  const v = String(raw ?? '').trim()
  if (v === 'member' || v === 'guest') return v
  return null
}

export function normalizeAgeInput(raw: string): string {
  const t = String(raw ?? '').replace(/\D/g, '').slice(0, 3)
  return t
}

export function choiceHasAgeRestriction(c: ActivityRegistrationOptionChoice): boolean {
  return c.ageMinInclusive != null && c.ageMaxInclusive != null
}

export function choiceHasGenderRestriction(c: ActivityRegistrationOptionChoice): boolean {
  return c.gender === '男' || c.gender === '女'
}

export function choiceHasParticipantAudienceRestriction(c: ActivityRegistrationOptionChoice): boolean {
  return c.participantAudience === 'member' || c.participantAudience === 'guest'
}

/** 任一侧有值即生效；散客一律不可选 */
export function choiceHasPointsRestriction(c: ActivityRegistrationOptionChoice): boolean {
  return c.pointsMinInclusive != null || c.pointsMaxInclusive != null
}

function clampPointsBoundValue(n: number): number {
  if (!Number.isFinite(n)) return 0
  if (n < 0) return 0
  if (n > 99_999_999) return 99_999_999
  return Math.floor(n)
}

/** 表单：年龄条件编辑区是否展开（含未完成填写） */
export function choiceAgeLimitEditing(c: ActivityRegistrationOptionChoice): boolean {
  return Boolean(c.ageLimitEditing)
}

/** 表单：性别条件编辑区是否展开 */
export function choiceGenderLimitEditing(c: ActivityRegistrationOptionChoice): boolean {
  return Boolean(c.genderLimitEditing)
}

/** 表单：参与对象条件编辑区是否展开 */
export function choiceParticipantAudienceLimitEditing(c: ActivityRegistrationOptionChoice): boolean {
  return Boolean(c.participantAudienceLimitEditing)
}

export function choiceAgeMinDisplayText(c: ActivityRegistrationOptionChoice): string {
  if (c.ageMinText != null) return c.ageMinText
  return c.ageMinInclusive != null ? String(c.ageMinInclusive) : ''
}

export function choiceAgeMaxDisplayText(c: ActivityRegistrationOptionChoice): string {
  if (c.ageMaxText != null) return c.ageMaxText
  return c.ageMaxInclusive != null ? String(c.ageMaxInclusive) : ''
}

export function enableAgeLimitEditing(ch: ActivityRegistrationOptionChoice): ActivityRegistrationOptionChoice {
  const minText = ch.ageMinText ?? (ch.ageMinInclusive != null ? String(ch.ageMinInclusive) : '18')
  const maxText = ch.ageMaxText ?? (ch.ageMaxInclusive != null ? String(ch.ageMaxInclusive) : '65')
  return {
    ...ch,
    ageLimitEditing: true,
    ageMinText: minText,
    ageMaxText: maxText,
    ageMinInclusive: ch.ageMinInclusive ?? 18,
    ageMaxInclusive: ch.ageMaxInclusive ?? 65,
  }
}

export function clearAgeLimitEditing(ch: ActivityRegistrationOptionChoice): ActivityRegistrationOptionChoice {
  return {
    ...ch,
    ageLimitEditing: false,
    ageMinText: '',
    ageMaxText: '',
    ageMinInclusive: null,
    ageMaxInclusive: null,
  }
}

/** 年龄条件选「不限」：清空范围但保持编辑区展开 */
export function clearAgeLimitValues(ch: ActivityRegistrationOptionChoice): ActivityRegistrationOptionChoice {
  return {
    ...ch,
    ageLimitEditing: true,
    ageMinText: '',
    ageMaxText: '',
    ageMinInclusive: null,
    ageMaxInclusive: null,
  }
}

export function applyAgeMinInput(ch: ActivityRegistrationOptionChoice, raw: string): ActivityRegistrationOptionChoice {
  const text = normalizeAgeInput(raw)
  const n = text === '' ? null : Number(text)
  return {
    ...ch,
    ageLimitEditing: true,
    ageMinText: text,
    ageMinInclusive: n != null && Number.isFinite(n) ? n : null,
  }
}

export function applyAgeMaxInput(ch: ActivityRegistrationOptionChoice, raw: string): ActivityRegistrationOptionChoice {
  const text = normalizeAgeInput(raw)
  const n = text === '' ? null : Number(text)
  return {
    ...ch,
    ageLimitEditing: true,
    ageMaxText: text,
    ageMaxInclusive: n != null && Number.isFinite(n) ? n : null,
  }
}

export function enableGenderLimitEditing(ch: ActivityRegistrationOptionChoice): ActivityRegistrationOptionChoice {
  return { ...ch, genderLimitEditing: true }
}

export function clearGenderLimitEditing(ch: ActivityRegistrationOptionChoice): ActivityRegistrationOptionChoice {
  return { ...ch, genderLimitEditing: false, gender: null }
}

export function enableParticipantAudienceLimitEditing(
  ch: ActivityRegistrationOptionChoice,
): ActivityRegistrationOptionChoice {
  return { ...ch, participantAudienceLimitEditing: true }
}

export function clearParticipantAudienceLimitEditing(
  ch: ActivityRegistrationOptionChoice,
): ActivityRegistrationOptionChoice {
  return { ...ch, participantAudienceLimitEditing: false, participantAudience: null }
}

export function choiceHasAnyRestriction(c: ActivityRegistrationOptionChoice): boolean {
  return (
    choiceHasAgeRestriction(c) ||
    choiceHasGenderRestriction(c) ||
    choiceHasParticipantAudienceRestriction(c) ||
    choiceHasPointsRestriction(c)
  )
}

export function isRegistrationOptionChoiceEligible(
  c: ActivityRegistrationOptionChoice,
  subject: RegistrationOptionSubject,
): boolean {
  if (choiceHasAgeRestriction(c)) {
    if (subject.ageYears == null) return false
    const lo = Number(c.ageMinInclusive)
    const hi = Number(c.ageMaxInclusive)
    if (subject.ageYears < lo || subject.ageYears > hi) return false
  }
  if (choiceHasGenderRestriction(c)) {
    if (!subject.gender) return false
    if (subject.gender !== c.gender) return false
  }
  if (choiceHasParticipantAudienceRestriction(c)) {
    if (!subject.participantType) return false
    if (c.participantAudience !== subject.participantType) return false
  }
  if (choiceHasPointsRestriction(c)) {
    if (subject.pointsBalance == null || !Number.isFinite(Number(subject.pointsBalance))) return false
    const pts = Math.floor(Number(subject.pointsBalance))
    if (c.pointsMinInclusive != null && pts < Number(c.pointsMinInclusive)) return false
    if (c.pointsMaxInclusive != null && pts > Number(c.pointsMaxInclusive)) return false
  }
  return true
}

export function registrationOptionChoiceHint(c: ActivityRegistrationOptionChoice): string | null {
  const parts: string[] = []
  if (choiceHasParticipantAudienceRestriction(c)) {
    parts.push(c.participantAudience === 'member' ? '限会员' : '限散客')
  }
  if (choiceHasAgeRestriction(c)) {
    parts.push(`限 ${c.ageMinInclusive}–${c.ageMaxInclusive} 岁`)
  }
  if (choiceHasGenderRestriction(c)) {
    parts.push(`限${c.gender}性`)
  }
  if (choiceHasPointsRestriction(c)) {
    const lo = c.pointsMinInclusive
    const hi = c.pointsMaxInclusive
    if (lo != null && hi != null) parts.push(`限积分 ${lo}～${hi}`)
    else if (lo != null) parts.push(`限积分 ≥${lo}`)
    else if (hi != null) parts.push(`限积分 ≤${hi}`)
  }
  return parts.length ? parts.join(' · ') : null
}

export function questionHasEligibleChoice(
  q: ActivityRegistrationOptionQuestion,
  subject: RegistrationOptionSubject,
): boolean {
  return q.choices.some((c) => isRegistrationOptionChoiceEligible(c, subject))
}

/** 活动日当天周岁 + 性别；eventDateYmd 为 yyyy-MM-dd 或 ISO 前缀 */
export function resolveRegistrationOptionSubjectFromIdNumber(
  idNumber: string,
  eventDateYmd: string,
): RegistrationOptionSubject {
  const id = String(idNumber ?? '').trim()
  const ref = String(eventDateYmd ?? '').trim().slice(0, 10)
  if (!id) return { ageYears: null, gender: null, participantType: null, pointsBalance: null }
  const birth = parseBirthDateFromCnId(id)
  const ageYears = birth && /^\d{4}-\d{2}-\d{2}$/.test(ref) ? ageYearsFromYmd(birth, ref) : null
  const gender = parseGenderFromCnId(id)
  return { ageYears, gender, participantType: null, pointsBalance: null }
}

export function buildRegistrationOptionSubject(
  idNumber: string,
  eventDateYmd: string,
  participantType: RegistrationOptionParticipantType,
): RegistrationOptionSubject {
  return {
    ...resolveRegistrationOptionSubjectFromIdNumber(idNumber, eventDateYmd),
    participantType,
  }
}

export function registrationOptionsSelectionComplete(
  cfg: ActivityRegistrationOptionsConfig,
  subject: RegistrationOptionSubject,
  selections: ActivityRegistrationOptionSelection[],
): boolean {
  if (!cfg.enabled) return true
  for (const q of cfg.items) {
    if (!questionHasEligibleChoice(q, subject)) continue
    const sel = selections.find((s) => s.questionId === q.id)
    if (!sel?.choiceId?.trim()) return false
    const choice = q.choices.find((c) => c.id === sel.choiceId)
    if (!choice || !isRegistrationOptionChoiceEligible(choice, subject)) return false
  }
  return true
}

export function newRegistrationOptionId(prefix: 'q' | 'c'): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`
}

export function emptyRegistrationOptionChoice(): ActivityRegistrationOptionChoice {
  return {
    id: newRegistrationOptionId('c'),
    label: '',
    chargeEnabled: false,
    amountYuanText: '',
    ageMinInclusive: null,
    ageMaxInclusive: null,
    gender: null,
    participantAudience: null,
    pointsMinInclusive: null,
    pointsMaxInclusive: null,
  }
}

export function emptyRegistrationOptionQuestion(): ActivityRegistrationOptionQuestion {
  return {
    id: newRegistrationOptionId('q'),
    title: '',
    choices: [emptyRegistrationOptionChoice(), { ...emptyRegistrationOptionChoice(), id: newRegistrationOptionId('c') }],
  }
}

function parseChoiceFromApi(ch: Record<string, unknown>): ActivityRegistrationOptionChoice {
  const chargeEnabled = Boolean(ch.chargeEnabled)
  const fen = Number(ch.amountFen ?? 0) || 0
  const yuanFromText = String(ch.amountYuanText ?? '').trim()
  const yuan =
    yuanFromText ||
    (chargeEnabled && fen > 0 ? (fen / 100).toFixed(2).replace(/\.?0+$/, '') : '')
  const ageMin = parseOptionalInt(ch.ageMinInclusive)
  const ageMax = parseOptionalInt(ch.ageMaxInclusive)
  const gender = parseOptionalGender(ch.gender)
  const participantAudience = parseOptionalParticipantAudience(ch.participantAudience)
  const pointsMinRaw = parseOptionalInt(ch.pointsMinInclusive)
  const pointsMaxRaw = parseOptionalInt(ch.pointsMaxInclusive)
  const pointsMin = pointsMinRaw == null ? null : clampPointsBoundValue(pointsMinRaw)
  const pointsMax = pointsMaxRaw == null ? null : clampPointsBoundValue(pointsMaxRaw)
  return {
    id: String(ch.id ?? newRegistrationOptionId('c')),
    label: String(ch.label ?? ''),
    chargeEnabled,
    amountYuanText: yuan,
    ageMinInclusive: ageMin,
    ageMaxInclusive: ageMax,
    gender,
    participantAudience,
    pointsMinInclusive: pointsMin,
    pointsMaxInclusive: pointsMax,
    ageLimitEditing: ageMin != null || ageMax != null,
    genderLimitEditing: gender != null,
    participantAudienceLimitEditing: participantAudience != null,
    ageMinText: ageMin != null ? String(ageMin) : '',
    ageMaxText: ageMax != null ? String(ageMax) : '',
  }
}

export function parseRegistrationOptionsFromApi(raw: unknown): ActivityRegistrationOptionsConfig {
  if (!raw || typeof raw !== 'object') {
    return { enabled: false, items: [] }
  }
  const o = raw as Record<string, unknown>
  const enabled = Boolean(o.enabled)
  const itemsRaw = Array.isArray(o.items) ? o.items : []
  const items: ActivityRegistrationOptionQuestion[] = itemsRaw.map((item) => {
    const q = item as Record<string, unknown>
    const choicesRaw = Array.isArray(q.choices) ? q.choices : []
    return {
      id: String(q.id ?? newRegistrationOptionId('q')),
      title: String(q.title ?? ''),
      choices: choicesRaw.map((c) => parseChoiceFromApi(c as Record<string, unknown>)),
    }
  })
  return { enabled, items }
}

function choiceToApiFields(c: ActivityRegistrationOptionChoice): Record<string, unknown> {
  const fen = c.chargeEnabled ? Math.max(0, Math.round(Number(c.amountYuanText || '0') * 100)) : 0
  const out: Record<string, unknown> = {
    id: c.id,
    label: c.label.trim(),
    chargeEnabled: c.chargeEnabled,
    amountFen: fen,
  }
  if (choiceHasAgeRestriction(c)) {
    out.ageMinInclusive = Number(c.ageMinInclusive)
    out.ageMaxInclusive = Number(c.ageMaxInclusive)
  }
  if (choiceHasGenderRestriction(c)) {
    out.gender = c.gender
  }
  if (choiceHasParticipantAudienceRestriction(c)) {
    out.participantAudience = c.participantAudience
  }
  if (choiceHasPointsRestriction(c)) {
    if (c.pointsMinInclusive != null) out.pointsMinInclusive = Number(c.pointsMinInclusive)
    if (c.pointsMaxInclusive != null) out.pointsMaxInclusive = Number(c.pointsMaxInclusive)
  }
  return out
}

export function registrationOptionsToApiBody(
  cfg: ActivityRegistrationOptionsConfig,
): Record<string, unknown> | null {
  if (!cfg.enabled) {
    return { enabled: false, items: [] }
  }
  return {
    enabled: true,
    items: cfg.items.map((q) => ({
      id: q.id,
      title: q.title.trim(),
      choices: q.choices.map((c) => choiceToApiFields(c)),
    })),
  }
}

/** 表单配置（amountYuanText）与 API 回显（amountFen）统一转为提交体，避免发布/下架时漏转 amountFen。 */
export function registrationOptionsForActivityApiBody(
  raw: ActivityRegistrationOptionsConfig | Record<string, unknown> | null | undefined,
): Record<string, unknown> | null {
  if (raw == null) return null
  const cfg = parseRegistrationOptionsFromApi(raw)
  const hasFormAmountText = cfg.items.some((q) =>
    q.choices.some((c) => String(c.amountYuanText ?? '').trim().length > 0),
  )
  const looksLikeApiShape =
    !hasFormAmountText &&
    Array.isArray((raw as Record<string, unknown>).items) &&
    ((raw as Record<string, unknown>).items as unknown[]).some((item) => {
      const choices = (item as Record<string, unknown>)?.choices
      return (
        Array.isArray(choices) &&
        choices.some((c) => typeof (c as Record<string, unknown>).amountFen === 'number')
      )
    })
  if (looksLikeApiShape) {
    return registrationOptionsToApiBody(parseRegistrationOptionsFromApi(raw))
  }
  return registrationOptionsToApiBody(cfg)
}

function validateChoiceConditions(c: ActivityRegistrationOptionChoice): string | null {
  const hasAge = choiceHasAgeRestriction(c)
  const hasPartialAge =
    (c.ageMinInclusive != null && c.ageMaxInclusive == null) ||
    (c.ageMinInclusive == null && c.ageMaxInclusive != null)
  if (c.ageLimitEditing && !hasAge) {
    if (hasPartialAge) return '年龄条件须同时填写下限与上限，或选择不限'
  }
  if (hasPartialAge) return '年龄条件须同时填写下限与上限，或选择不限'
  if (hasAge) {
    const lo = Number(c.ageMinInclusive)
    const hi = Number(c.ageMaxInclusive)
    if (!Number.isFinite(lo) || !Number.isFinite(hi)) return '年龄范围无效'
    if (lo < 0 || hi > 150 || lo > hi) return '年龄范围须在 0–150 周岁且下限不大于上限'
  }
  const g = c.gender
  if (g != null && g !== '男' && g !== '女') return '性别条件须为男或女'
  const pa = c.participantAudience
  if (pa != null && pa !== 'member' && pa !== 'guest') return '参与对象条件须为会员或散客'
  const pLo = c.pointsMinInclusive
  const pHi = c.pointsMaxInclusive
  if (pLo != null || pHi != null) {
    if (pLo != null && (!Number.isFinite(Number(pLo)) || Number(pLo) < 0 || Number(pLo) > 99_999_999)) {
      return '积分条件下限须在 0–99999999'
    }
    if (pHi != null && (!Number.isFinite(Number(pHi)) || Number(pHi) < 0 || Number(pHi) > 99_999_999)) {
      return '积分条件上限须在 0–99999999'
    }
    if (pLo != null && pHi != null && Number(pLo) > Number(pHi)) return '积分条件下限不能大于上限'
  }
  return null
}

export function validateRegistrationOptionsForm(cfg: ActivityRegistrationOptionsConfig): string | null {
  if (!cfg.enabled) return null
  if (cfg.items.length < 1) return '启用报名活动选项时须至少添加 1 个题目'
  if (cfg.items.length > MAX_REGISTRATION_OPTION_QUESTIONS) {
    return `报名活动选项最多 ${MAX_REGISTRATION_OPTION_QUESTIONS} 个题目`
  }
  for (const q of cfg.items) {
    if (!q.title.trim()) return '请填写每个活动选项的标题'
    if (q.choices.length < 2) return '每个活动选项至少需要 2 个选项值'
    if (q.choices.length > MAX_REGISTRATION_OPTION_CHOICES) {
      return `每个活动选项最多 ${MAX_REGISTRATION_OPTION_CHOICES} 个选项值`
    }
    for (const c of q.choices) {
      if (!c.label.trim()) return '请填写每个选项值的文案'
      if (c.chargeEnabled) {
        const fen = Math.round(Number(c.amountYuanText || '0') * 100)
        if (fen < 1) return '收费选项须填写有效金额（至少 0.01 元）'
      }
      const condErr = validateChoiceConditions(c)
      if (condErr) return condErr
    }
  }
  return null
}

export function computeOptionFeesFen(
  cfg: ActivityRegistrationOptionsConfig,
  selections: ActivityRegistrationOptionSelection[],
): number {
  if (!cfg.enabled) return 0
  let total = 0
  for (const q of cfg.items) {
    const sel = selections.find((s) => s.questionId === q.id)
    if (!sel) continue
    const choice = q.choices.find((c) => c.id === sel.choiceId)
    if (choice?.chargeEnabled) {
      total += Math.max(0, Math.round(Number(choice.amountYuanText || '0') * 100))
    }
  }
  return total
}

export function computeRegistrationTotalFen(
  expenseType: string,
  onlinePayAmountFen: number,
  cfg: ActivityRegistrationOptionsConfig,
  selections: ActivityRegistrationOptionSelection[],
  onlinePayDepositAmountFen = 0,
): number {
  const online = expenseType.trim() === '在线支付'
  const base = online ? Math.max(0, onlinePayAmountFen) : 0
  const deposit = online ? Math.max(0, onlinePayDepositAmountFen) : 0
  return base + deposit + computeOptionFeesFen(cfg, selections)
}

export function normalizeDigitYuanInput(raw: string): string {
  const t = String(raw ?? '').trim()
  if (!t) return ''
  const hit = t.match(/^(\d+)(\.(\d{0,2})?)?$/)
  if (!hit) return t.slice(0, -1)
  return `${hit[1]}${hit[2] ?? ''}`
}
